// ─────────────────────────────────────────────────────────────────────────────
// SOURCE DE VÉRITÉ UNIQUE pour les produits / prix Stripe et la définition des plans.
// Tout le reste de l'app (Pricing, constants.PLANS, Affiliate…) dérive de ce fichier.
//
// ⚠️ Prix "Basic mensuel" = 0,50 € : c'est le prix RÉELLEMENT facturé par Stripe
//    aujourd'hui (la table `subscriptions.amount` stocke 50 centimes). C'est très
//    probablement un prix de TEST — à relever côté Stripe quand tu seras prêt, puis
//    mettre à jour `price` ici. Ne PAS toucher au webhook pour ça.
// ─────────────────────────────────────────────────────────────────────────────

export type PlanTier = 'basic' | 'pro' | 'premium'
export type BillingPeriod = 'monthly' | 'annual'

export interface StripeProduct {
  id: string
  priceId: string
  name: string
  description: string
  /** Prix en euros (unité, pas en centimes). */
  price: number
  currency: string
  mode: 'subscription' | 'payment'
  billing_period: BillingPeriod
  tier: PlanTier
}

export const stripeProducts: StripeProduct[] = [
  // ── Basic ──────────────────────────────────────────────────────────────────
  {
    id: 'prod_TdA5nKuJxnbPto',
    priceId: 'price_1SkBb6QWJUq678NLoQvTWbdD',
    name: 'Basic Monthly',
    description: 'Abonnement TrackMarkete',
    price: 0.50, // ⚠️ prix de test — voir avertissement en tête de fichier
    currency: 'eur',
    mode: 'subscription',
    billing_period: 'monthly',
    tier: 'basic',
  },
  {
    id: 'prod_TdBHa1p0kdeADf',
    priceId: 'price_1SfuvGQWJUq678NLfz7ZTuc1',
    name: 'Basic Annual',
    description: 'Abonnement TrackMarkete',
    price: 192.0,
    currency: 'eur',
    mode: 'subscription',
    billing_period: 'annual',
    tier: 'basic',
  },
  // ── Pro ────────────────────────────────────────────────────────────────────
  {
    id: 'prod_TdBRFtCAtCP153',
    priceId: 'price_1Sfv4uQWJUq678NLFNzilWOd',
    name: 'Pro Monthly',
    description: 'Abonnement TrackMarkete',
    price: 30.0,
    currency: 'eur',
    mode: 'subscription',
    billing_period: 'monthly',
    tier: 'pro',
  },
  {
    id: 'prod_TdBSWWIKXMXI92',
    priceId: 'price_1Sfv5uQWJUq678NLoYd80Gzp',
    name: 'Pro Annual',
    description: 'Abonnement TrackMarkete',
    price: 336.0,
    currency: 'eur',
    mode: 'subscription',
    billing_period: 'annual',
    tier: 'pro',
  },
  // ── Premium ──────────────────────────────────────────────────────────────────
  {
    id: 'prod_TdBUI3KaQx9jZp',
    priceId: 'price_1Sfv7rQWJUq678NLu5ppUFp2',
    name: 'Premium Monthly',
    description: 'Abonnement TrackMarkete',
    price: 80.0,
    currency: 'eur',
    mode: 'subscription',
    billing_period: 'monthly',
    tier: 'premium',
  },
  {
    id: 'prod_TdBWOK5W2hss9s',
    priceId: 'price_1Sfv96QWJUq678NLAsRzlVJN',
    name: 'Premium Annual',
    description: 'Abonnement TrackMarkete',
    price: 768.0,
    currency: 'eur',
    mode: 'subscription',
    billing_period: 'annual',
    tier: 'premium',
  },
]

// ── Métadonnées produit (features / limites / libellés) par tier ──────────────

export interface PlanMeta {
  id: PlanTier
  name: string
  description: string
  popular?: boolean
  features: string[]
  limits: { accounts: number; history: number }
}

export const PLAN_META: Record<PlanTier, PlanMeta> = {
  basic: {
    id: 'basic',
    name: 'Basic',
    description: 'Pour débuter',
    features: [
      '1 compte MT4/MT5',
      'Synchronisation automatique',
      'Analyses de base',
      'Historique 6 mois',
      'Support email',
    ],
    limits: { accounts: 1, history: 6 },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'Le plus populaire',
    popular: true,
    features: [
      '2 comptes MT4/MT5',
      'Tout de Basic +',
      'Analyses avancées',
      'Heatmaps',
      'Export CSV',
      'Historique illimité',
      'Support prioritaire',
    ],
    limits: { accounts: 2, history: -1 },
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    description: 'Pour professionnels',
    features: [
      'Comptes illimités',
      'Tout de Pro +',
      'API Access',
      'White-label (sur demande)',
      'Support 24/7',
      'Manager dédié',
    ],
    limits: { accounts: -1, history: -1 },
  },
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export const getProductByPriceId = (priceId: string): StripeProduct | undefined =>
  stripeProducts.find((p) => p.priceId === priceId)

export const getProductsByTier = (tier: PlanTier): StripeProduct[] =>
  stripeProducts.filter((p) => p.tier === tier)

export const getProduct = (tier: PlanTier, period: BillingPeriod): StripeProduct | undefined =>
  stripeProducts.find((p) => p.tier === tier && p.billing_period === period)

export const formatPrice = (price: number, currency: string): string => {
  const symbol = currency === 'eur' ? '€' : '$'
  return `${symbol}${price.toFixed(2)}`
}

/** Représentation consolidée d'un plan (méta + prix + price IDs) dérivée de la source unique. */
export interface ResolvedPlan extends PlanMeta {
  price: { monthly: number; yearly: number }
  stripePriceId: { monthly: string; yearly: string }
}

export const getPlan = (tier: PlanTier): ResolvedPlan => {
  const monthly = getProduct(tier, 'monthly')
  const annual = getProduct(tier, 'annual')
  return {
    ...PLAN_META[tier],
    price: { monthly: monthly?.price ?? 0, yearly: annual?.price ?? 0 },
    stripePriceId: { monthly: monthly?.priceId ?? '', yearly: annual?.priceId ?? '' },
  }
}
